import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, MapPin, Clock, Users, ArrowRight, ArrowLeft, Share2,
  Ticket, CreditCard, Check, AlertCircle, User, Building2,
  Mail, Phone, Globe, Minus, Plus, Tag, QrCode, Download,
  ChevronDown, ChevronUp, Loader2, Bell
} from 'lucide-react';
import MegaMenu from '../../components/public/MegaMenu';
import Footer from '../../components/public/Footer';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import DynamicFormRenderer, { validateFormFields, evaluateConditions, COUNTRIES } from '../../components/registration/DynamicFormRenderer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Waitlist Modal Component
const WaitlistModal = ({ event, isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    quantity_requested: 1
  });
  const [success, setSuccess] = useState(false);
  const [position, setPosition] = useState(null);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await fetch(`${API}/universal-events/events/${event.id}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(true);
        setPosition(data.entry.position);
        toast.success(`You're #${data.entry.position} on the waitlist!`);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to join waitlist');
      }
    } catch (err) {
      toast.error('Failed to join waitlist');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        className="bg-white rounded-xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {success ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">You're on the waitlist!</h3>
            <p className="text-gray-600 mb-4">
              You're position <span className="font-bold text-green-600">#{position}</span> in line.
              We'll notify you at <span className="font-medium">{formData.email}</span> when a spot opens up.
            </p>
            <Button onClick={onClose} className="w-full">Done</Button>
          </div>
        ) : (
          <>
            <div className="p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Bell className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Join the Waitlist</h3>
                  <p className="text-sm text-gray-500">Get notified when spots open up</p>
                </div>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500"
                  placeholder="John Doe"
                  data-testid="waitlist-name"
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500"
                  placeholder="john@example.com"
                  data-testid="waitlist-email"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone (Optional)</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500"
                  placeholder="+254 712 345 678"
                  data-testid="waitlist-phone"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">How many tickets do you need?</label>
                <select
                  value={formData.quantity_requested}
                  onChange={(e) => setFormData(prev => ({ ...prev, quantity_requested: parseInt(e.target.value) }))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500"
                  data-testid="waitlist-quantity"
                >
                  {[1, 2, 3, 4, 5].map(n => (
                    <option key={n} value={n}>{n} ticket{n > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} className="flex-1" data-testid="waitlist-submit">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    <>
                      <Bell className="w-4 h-4 mr-2" />
                      Join Waitlist
                    </>
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

const EventDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  // Data states
  const [event, setEvent] = useState(null);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  
  // Checkout states
  const [step, setStep] = useState(1); // 1: Info, 2: Tickets, 3: Details, 4: Payment, 5: Confirmation
  const [selectedTickets, setSelectedTickets] = useState({});
  const [buyerCountry, setBuyerCountry] = useState('Kenya');
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  
  // Buyer info
  const [buyerInfo, setBuyerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    organization: ''
  });
  
  // Attendee details
  const [attendees, setAttendees] = useState([]);
  
  // Order state
  const [order, setOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [mpesaPhone, setMpesaPhone] = useState('');
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('paybill'); // 'stk' or 'paybill'
  const [paybillInstructions, setPaybillInstructions] = useState(null);
  const [mpesaReference, setMpesaReference] = useState('');
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  // Fetch event data
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await fetch(`${API}/universal-events/events/${slug}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError('Event not found');
          } else {
            setError('Failed to load event');
          }
          return;
        }
        const data = await response.json();
        setEvent(data);
        
        // Fetch ticket types with pricing
        const ticketsRes = await fetch(
          `${API}/universal-events/events/${data.id}/ticket-types?buyer_country=${buyerCountry}`
        );
        if (ticketsRes.ok) {
          const ticketsData = await ticketsRes.json();
          setTicketTypes(ticketsData);
          
          // Initialize selected tickets
          const initial = {};
          ticketsData.forEach(t => {
            initial[t.id] = 0;
          });
          setSelectedTickets(initial);
        }
      } catch (err) {
        console.error('Error fetching event:', err);
        setError('Failed to load event');
      } finally {
        setLoading(false);
      }
    };
    
    fetchEvent();
  }, [slug]);

  // Update ticket prices when country changes
  useEffect(() => {
    if (!event) return;
    
    const updatePrices = async () => {
      try {
        const ticketsRes = await fetch(
          `${API}/universal-events/events/${event.id}/ticket-types?buyer_country=${buyerCountry}`
        );
        if (ticketsRes.ok) {
          const ticketsData = await ticketsRes.json();
          setTicketTypes(ticketsData);
        }
      } catch (err) {
        console.error('Error updating prices:', err);
      }
    };
    
    updatePrices();
  }, [buyerCountry, event]);

  // Calculate totals
  const subtotal = ticketTypes.reduce((sum, ticket) => {
    return sum + (selectedTickets[ticket.id] || 0) * ticket.price;
  }, 0);
  
  const discountAmount = promoDiscount?.discount_amount || 0;
  const total = subtotal - discountAmount;
  const totalTickets = Object.values(selectedTickets).reduce((a, b) => a + b, 0);

  // Validate promo code
  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;
    
    setValidatingPromo(true);
    try {
      const response = await fetch(
        `${API}/universal-events/events/${event.id}/validate-promo?code=${promoCode}&subtotal=${subtotal}&buyer_country=${buyerCountry}`,
        { method: 'POST' }
      );
      const data = await response.json();
      
      if (data.valid) {
        setPromoDiscount(data);
        toast.success(`Promo code applied: ${data.discount_type === 'percentage' ? data.discount_value + '%' : 'KES ' + data.discount_value} off!`);
      } else {
        setPromoDiscount(null);
        toast.error(data.error || 'Invalid promo code');
      }
    } catch (err) {
      toast.error('Failed to validate promo code');
    } finally {
      setValidatingPromo(false);
    }
  };

  // Update ticket quantity
  const updateTicketQuantity = (ticketId, delta) => {
    setSelectedTickets(prev => {
      const ticket = ticketTypes.find(t => t.id === ticketId);
      const currentQty = prev[ticketId] || 0;
      const newQty = Math.max(0, Math.min(currentQty + delta, ticket?.max_per_order || 10));
      return { ...prev, [ticketId]: newQty };
    });
    // Reset promo when tickets change
    setPromoDiscount(null);
  };

  // Initialize attendees when moving to details step
  useEffect(() => {
    if (step === 3) {
      const attendeeList = [];
      ticketTypes.forEach(ticket => {
        const qty = selectedTickets[ticket.id] || 0;
        for (let i = 0; i < qty; i++) {
          attendeeList.push({
            ticket_type_id: ticket.id,
            ticket_type_name: ticket.name,
            name: i === 0 ? buyerInfo.name : '',
            email: i === 0 ? buyerInfo.email : '',
            phone: i === 0 ? buyerInfo.phone : '',
            organization: i === 0 ? buyerInfo.organization : '',
            country: buyerCountry,
            job_title: '',
            custom_fields: {}
          });
        }
      });
      setAttendees(attendeeList);
    }
  }, [step]);

  // Update attendee
  const updateAttendee = (index, field, value) => {
    setAttendees(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Submit order
  const submitOrder = async () => {
    setSubmitting(true);
    try {
      const items = ticketTypes
        .filter(t => selectedTickets[t.id] > 0)
        .map(t => ({
          ticket_type_id: t.id,
          quantity: selectedTickets[t.id]
        }));

      const orderData = {
        event_id: event.id,
        buyer_name: buyerInfo.name,
        buyer_email: buyerInfo.email,
        buyer_phone: buyerInfo.phone,
        buyer_country: buyerCountry,
        buyer_organization: buyerInfo.organization,
        items,
        promo_code: promoDiscount ? promoCode : null,
        attendees: attendees.map(a => ({
          name: a.name,
          email: a.email,
          phone: a.phone,
          organization: a.organization,
          country: a.country,
          job_title: a.job_title,
          custom_fields: a.custom_fields
        }))
      };

      const response = await fetch(`${API}/universal-events/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to create order');
      }

      const data = await response.json();
      setOrder(data);
      
      // Fetch paybill instructions for the order
      await fetchPaybillInstructions(data.id);
      
      toast.success('Order created! Please complete payment.');
      return data; // Return data for caller
    } catch (err) {
      console.error('Order error:', err);
      toast.error(err.message || 'Failed to create order');
      throw err; // Re-throw so caller knows it failed
    } finally {
      setSubmitting(false);
    }
  };

  // Initiate M-Pesa payment
  const initiateMpesaPayment = async () => {
    if (!order || !mpesaPhone) return;
    
    setPaymentProcessing(true);
    try {
      // Format phone number
      let phone = mpesaPhone.trim().replace(/\s/g, '');
      if (phone.startsWith('0')) {
        phone = '254' + phone.slice(1);
      } else if (phone.startsWith('+')) {
        phone = phone.slice(1);
      }
      
      const response = await fetch(
        `${API}/universal-events/orders/${order.id}/pay/mpesa?phone_number=${phone}`,
        { method: 'POST' }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Check your phone for M-Pesa prompt!');
        // Start polling for payment status
        pollPaymentStatus(order.id);
      } else {
        toast.error(data.error || 'Failed to initiate payment');
      }
    } catch (err) {
      console.error('M-Pesa error:', err);
      toast.error('Failed to initiate M-Pesa payment');
    } finally {
      setPaymentProcessing(false);
    }
  };

  // Poll for payment status
  const pollPaymentStatus = async (orderId) => {
    let attempts = 0;
    const maxAttempts = 30; // 1 minute
    
    const checkStatus = async () => {
      try {
        const response = await fetch(`${API}/universal-events/orders/${orderId}`);
        const orderData = await response.json();
        
        if (orderData.payment_status === 'paid') {
          setOrder(orderData);
          setStep(5);
          toast.success('Payment confirmed!');
          return;
        }
        
        if (orderData.payment_status === 'failed') {
          toast.error('Payment failed. Please try again.');
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 2000);
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
      }
    };
    
    checkStatus();
  };

  // Fetch Paybill instructions
  const fetchPaybillInstructions = async (orderId) => {
    try {
      const response = await fetch(`${API}/universal-events/orders/${orderId}/paybill-instructions`);
      if (response.ok) {
        const data = await response.json();
        setPaybillInstructions(data);
      }
    } catch (err) {
      console.error('Error fetching paybill instructions:', err);
    }
  };

  // Confirm Paybill payment - Instant Offline Ticket Issuance
  const confirmPaybillPayment = async () => {
    if (!order || !mpesaReference.trim()) {
      toast.error('Please enter your M-Pesa reference code');
      return;
    }

    setConfirmingPayment(true);
    try {
      // Use the instant offline payment endpoint for immediate ticket issuance
      const response = await fetch(`${API}/universal-events/orders/${order.id}/pay/mpesa/paybill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_phone: mpesaPhone || buyerInfo.phone,
          mpesa_reference: mpesaReference.trim(),
          send_sms: true,
          send_whatsapp: false
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Payment confirmed! Your ticket is ready.');
        
        // Redirect to ticket view page
        if (data.ticket_url) {
          window.location.href = `/tickets/${order.id}`;
        } else {
          setStep(5); // Fallback to confirmation step
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to confirm payment');
      }
    } catch (err) {
      toast.error('Failed to confirm payment');
    } finally {
      setConfirmingPayment(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <MegaMenu />
        <div className="pt-32 pb-20 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">{error}</h1>
          <p className="text-white/60 mb-6">The event you're looking for doesn't exist or has been removed.</p>
          <Link to="/events">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Events
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="event-detail-page">
      <MegaMenu />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-12">
        {event.hero_image_url && (
          <div className="absolute inset-0 z-0">
            <img 
              src={event.hero_image_url} 
              alt={event.name}
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background via-background/90 to-background" />
          </div>
        )}
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/events" className="inline-flex items-center text-white/60 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Events
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid lg:grid-cols-3 gap-8"
          >
            {/* Event Info */}
            <div className="lg:col-span-2">
              {event.event_type && (
                <span className="inline-block px-3 py-1 bg-primary/20 text-primary text-sm font-medium rounded-full mb-4">
                  {event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1)}
                </span>
              )}
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4" data-testid="event-title">
                {event.name}
              </h1>
              
              {event.short_description && (
                <p className="text-lg text-white/70 mb-6">{event.short_description}</p>
              )}
              
              <div className="flex flex-wrap gap-4 text-white/80">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  <span>{formatDate(event.start_date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  <span>{formatTime(event.start_date)} - {formatTime(event.end_date)}</span>
                </div>
                {event.venue_name && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    <span>{event.venue_name}, {event.city}</span>
                  </div>
                )}
                {event.max_attendees && (
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary" />
                    <span>{event.current_attendees}/{event.max_attendees} registered</span>
                    {event.current_attendees >= event.max_attendees && (
                      <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                        Full
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Quick Stats Card */}
            <div className="bg-card-bg border border-border rounded-xl overflow-hidden">
              {/* Event Poster/Banner in Sidebar */}
              {event.hero_image_url && (
                <div className="relative bg-black/20">
                  <img 
                    src={event.hero_image_url} 
                    alt={event.name}
                    className="w-full h-auto max-h-[400px] object-contain"
                  />
                </div>
              )}
              
              <div className="p-6">
                <div className="text-center mb-6">
                  <p className="text-white/60 text-sm mb-1">Starting from</p>
                  <p className="text-3xl font-bold text-white">
                    KES {Math.min(...ticketTypes.map(t => t.price)).toLocaleString()}
                  </p>
                </div>
              
              {/* Check if event is at capacity */}
              {event.max_attendees && event.current_attendees >= event.max_attendees ? (
                <div className="space-y-3">
                  <div className="text-center p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-yellow-400 text-sm font-medium">This event is at capacity</p>
                  </div>
                  <Button 
                    className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-semibold"
                    onClick={() => setShowWaitlistModal(true)}
                    data-testid="join-waitlist-btn"
                  >
                    <Bell className="w-4 h-4 mr-2" />
                    Join Waitlist
                  </Button>
                </div>
              ) : event.status === 'published' || event.status === 'registration_open' ? (
                <Button 
                  className="w-full bg-primary hover:bg-primary/90 text-black font-semibold"
                  onClick={() => {
                    // Scroll to ticket selection
                    const ticketSection = document.querySelector('[data-testid="ticket-selection"]');
                    if (ticketSection) {
                      ticketSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      // Highlight the section briefly
                      ticketSection.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
                      setTimeout(() => ticketSection.classList.remove('ring-2', 'ring-primary', 'ring-offset-2'), 2000);
                    }
                  }}
                  data-testid="register-now-btn"
                >
                  <Ticket className="w-4 h-4 mr-2" />
                  Register Now
                </Button>
              ) : (
                <Button className="w-full" disabled>
                  Registration Closed
                </Button>
              )}
              
              <div className="mt-4 flex justify-center gap-4">
                <button className="text-white/60 hover:text-white transition-colors" title="Share">
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
              </div>
              
              {/* Partner Logos */}
              {event.partner_logos_url && (
                <div className="p-4 bg-white/5 border-t border-border">
                  <p className="text-center text-xs text-white/50 uppercase tracking-wider mb-3">In Partnership With</p>
                  <div className="flex flex-wrap justify-center items-center gap-4">
                    {event.partner_logos_url.split(',').filter(Boolean).map((logoUrl, idx) => (
                      <img 
                        key={idx}
                        src={logoUrl.trim()} 
                        alt={`Partner ${idx + 1}`} 
                        className="h-12 max-w-[120px] object-contain opacity-80 hover:opacity-100 transition-opacity"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Event Details */}
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              {event.description && (
                <div className="bg-card-bg border border-border rounded-xl p-6">
                  <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                    <span className="w-1 h-6 bg-primary rounded-full"></span>
                    About This Event
                  </h2>
                  <div 
                    className="prose prose-invert max-w-none text-white/80 leading-relaxed space-y-4 [&>p]:mb-4 [&>ul]:space-y-2 [&>ol]:space-y-2"
                    style={{ lineHeight: '1.8' }}
                    dangerouslySetInnerHTML={{ __html: event.description }}
                  />
                </div>
              )}
              
              {/* Speakers */}
              {event.speakers?.length > 0 && (
                <div className="bg-card-bg border border-border rounded-xl p-6">
                  <h2 className="text-xl font-semibold text-white mb-4">Speakers</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {event.speakers.map(speaker => (
                      <div key={speaker.id} className="flex items-start gap-4 p-4 bg-background rounded-lg">
                        {speaker.photo_url ? (
                          <img src={speaker.photo_url} alt={speaker.name} className="w-16 h-16 rounded-full object-cover" />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                            <User className="w-8 h-8 text-primary" />
                          </div>
                        )}
                        <div>
                          <h3 className="font-semibold text-white">{speaker.name}</h3>
                          <p className="text-sm text-white/60">{speaker.title}</p>
                          {speaker.organization && (
                            <p className="text-sm text-white/40">{speaker.organization}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* FAQs */}
              {event.faqs?.length > 0 && (
                <div className="bg-card-bg border border-border rounded-xl p-6">
                  <h2 className="text-xl font-semibold text-white mb-4">FAQs</h2>
                  <div className="space-y-4">
                    {event.faqs.map((faq, idx) => (
                      <div key={idx} className="border-b border-border pb-4 last:border-0">
                        <h3 className="font-medium text-white mb-2">{faq.question}</h3>
                        <p className="text-white/60">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Right Column - Ticket Selection */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Ticket Types */}
                <div className="bg-card-bg border border-border rounded-xl p-6" data-testid="ticket-selection">
                  <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                    <Ticket className="w-5 h-5 text-primary" />
                    Select Tickets
                  </h2>
                  
                  {/* Country Selection */}
                  <div className="mb-4">
                    <label className="block text-sm text-white/60 mb-2">Your Country</label>
                    <select
                      value={buyerCountry}
                      onChange={(e) => setBuyerCountry(e.target.value)}
                      className="w-full bg-background border border-border rounded-lg px-4 py-2 text-white"
                      data-testid="country-select"
                    >
                      {COUNTRIES.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="space-y-4">
                    {ticketTypes.map(ticket => (
                      <div 
                        key={ticket.id} 
                        className={`p-4 border rounded-lg transition-all ${
                          ticket.is_available 
                            ? 'border-border hover:border-primary/50' 
                            : 'border-border/50 opacity-50'
                        }`}
                        data-testid={`ticket-type-${ticket.id}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="font-semibold text-white">{ticket.name}</h3>
                            {ticket.description && (
                              <p className="text-sm text-white/60">{ticket.description}</p>
                            )}
                          </div>
                          <div className="text-right">
                            {ticket.discount_amount > 0 ? (
                              <>
                                <p className="text-lg font-bold text-primary">
                                  {ticket.currency} {ticket.price.toLocaleString()}
                                </p>
                                <p className="text-sm text-white/50 line-through">
                                  {ticket.currency} {ticket.original_price.toLocaleString()}
                                </p>
                                {ticket.discount_reason && (
                                  <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded">
                                    {ticket.discount_reason}
                                  </span>
                                )}
                              </>
                            ) : (
                              <p className="text-lg font-bold text-primary">
                                {ticket.currency} {ticket.price.toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {ticket.benefits?.length > 0 && (
                          <ul className="text-sm text-white/60 mb-3 space-y-1">
                            {ticket.benefits.slice(0, 3).map((b, i) => (
                              <li key={i} className="flex items-center gap-2">
                                <Check className="w-3 h-3 text-green-500" />
                                {b}
                              </li>
                            ))}
                          </ul>
                        )}
                        
                        {ticket.is_available ? (
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => updateTicketQuantity(ticket.id, -1)}
                              className="w-8 h-8 rounded-full bg-background border border-border flex items-center justify-center text-white hover:border-primary transition-colors"
                              disabled={selectedTickets[ticket.id] === 0}
                              data-testid={`ticket-minus-${ticket.id}`}
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-8 text-center text-white font-semibold" data-testid={`ticket-qty-${ticket.id}`}>
                              {selectedTickets[ticket.id] || 0}
                            </span>
                            <button
                              onClick={() => updateTicketQuantity(ticket.id, 1)}
                              className="w-8 h-8 rounded-full bg-background border border-border flex items-center justify-center text-white hover:border-primary transition-colors"
                              disabled={selectedTickets[ticket.id] >= ticket.max_per_order}
                              data-testid={`ticket-plus-${ticket.id}`}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                            {ticket.quantity_remaining !== null && (
                              <span className="text-xs text-white/40 ml-auto">
                                {ticket.quantity_remaining} left
                              </span>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-red-400">Sold Out</p>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Promo Code */}
                  <div className="mt-4 pt-4 border-t border-border">
                    <label className="block text-sm text-white/60 mb-2">Promo Code</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        placeholder="Enter code"
                        className="flex-1 bg-background border border-border rounded-lg px-4 py-2 text-white"
                        data-testid="promo-code-input"
                      />
                      <Button
                        variant="outline"
                        onClick={validatePromoCode}
                        disabled={validatingPromo || !promoCode}
                        data-testid="apply-promo-btn"
                      >
                        {validatingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                      </Button>
                    </div>
                    {promoDiscount && (
                      <p className="text-sm text-green-500 mt-2 flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        {promoDiscount.code} applied: -{promoDiscount.currency || 'KES'} {promoDiscount.discount_amount.toLocaleString()}
                      </p>
                    )}
                  </div>
                  
                  {/* Totals */}
                  {totalTickets > 0 && (
                    <div className="mt-4 pt-4 border-t border-border space-y-2">
                      <div className="flex justify-between text-white/60">
                        <span>Subtotal ({totalTickets} tickets)</span>
                        <span>KES {subtotal.toLocaleString()}</span>
                      </div>
                      {discountAmount > 0 && (
                        <div className="flex justify-between text-green-500">
                          <span>Discount</span>
                          <span>-KES {discountAmount.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-white font-semibold text-lg">
                        <span>Total</span>
                        <span data-testid="order-total">KES {total.toLocaleString()}</span>
                      </div>
                      
                      <Button
                        className="w-full mt-4 bg-primary hover:bg-primary/90 text-black font-semibold"
                        onClick={() => setStep(3)}
                        data-testid="proceed-checkout-btn"
                      >
                        Proceed to Checkout
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Contact Info */}
                {(event.contact_email || event.contact_phone) && (
                  <div className="bg-card-bg border border-border rounded-xl p-6">
                    <h3 className="font-semibold text-white mb-3">Contact</h3>
                    {event.contact_person && (
                      <p className="text-white/80 mb-2">{event.contact_person}</p>
                    )}
                    {event.contact_email && (
                      <a href={`mailto:${event.contact_email}`} className="flex items-center gap-2 text-white/60 hover:text-primary mb-2">
                        <Mail className="w-4 h-4" />
                        {event.contact_email}
                      </a>
                    )}
                    {event.contact_phone && (
                      <a href={`tel:${event.contact_phone}`} className="flex items-center gap-2 text-white/60 hover:text-primary">
                        <Phone className="w-4 h-4" />
                        {event.contact_phone}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Checkout Modal */}
      <AnimatePresence>
        {step >= 3 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => step < 5 && setStep(1)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card-bg border border-border rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="checkout-modal"
            >
              {/* Step 3: Attendee Details */}
              {step === 3 && (
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-white mb-6">Attendee Details</h2>
                  
                  {/* Buyer Info */}
                  <div className="mb-6 p-4 bg-background rounded-lg">
                    <h3 className="font-medium text-white mb-4">Your Information (Buyer)</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-white/60 mb-1">Full Name *</label>
                        <input
                          type="text"
                          value={buyerInfo.name}
                          onChange={(e) => setBuyerInfo(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                          required
                          data-testid="buyer-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-white/60 mb-1">Email *</label>
                        <input
                          type="email"
                          value={buyerInfo.email}
                          onChange={(e) => setBuyerInfo(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                          required
                          data-testid="buyer-email"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-white/60 mb-1">Phone</label>
                        <input
                          type="tel"
                          value={buyerInfo.phone}
                          onChange={(e) => setBuyerInfo(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                          data-testid="buyer-phone"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-white/60 mb-1">Organization</label>
                        <input
                          type="text"
                          value={buyerInfo.organization}
                          onChange={(e) => setBuyerInfo(prev => ({ ...prev, organization: e.target.value }))}
                          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                          data-testid="buyer-organization"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Attendee Details - Using Dynamic Registration Fields */}
                  {attendees.length > 0 && (
                    <div className="space-y-4">
                      <h3 className="font-medium text-white">Attendee Information</h3>
                      {attendees.map((attendee, idx) => (
                        <div key={idx} className="p-4 bg-background rounded-lg">
                          <div className="flex items-center gap-2 mb-4">
                            <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-sm flex items-center justify-center">
                              {idx + 1}
                            </span>
                            <span className="text-white font-medium">{attendee.ticket_type_name}</span>
                          </div>
                          
                          {/* Default Fields */}
                          <div className="grid sm:grid-cols-2 gap-4 mb-4">
                            <div>
                              <label className="block text-sm text-white/60 mb-1">Full Name *</label>
                              <input
                                type="text"
                                value={attendee.name}
                                onChange={(e) => updateAttendee(idx, 'name', e.target.value)}
                                className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                                required
                                data-testid={`attendee-name-${idx}`}
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-white/60 mb-1">Email *</label>
                              <input
                                type="email"
                                value={attendee.email}
                                onChange={(e) => updateAttendee(idx, 'email', e.target.value)}
                                className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                                required
                                data-testid={`attendee-email-${idx}`}
                              />
                            </div>
                          </div>
                          
                          {/* Dynamic Registration Fields from Event Config */}
                          {event?.registration_fields && event.registration_fields.length > 0 && (
                            <div className="border-t border-border pt-4 mt-4">
                              <p className="text-sm text-white/60 mb-3">Additional Information</p>
                              <div className="grid sm:grid-cols-2 gap-4">
                                {event.registration_fields
                                  .filter(f => !['full_name', 'email', 'name'].includes(f.field_name))
                                  .filter(f => evaluateConditions(f.conditional_rules, attendee.custom_fields))
                                  .map((field, fidx) => (
                                    <div key={fidx}>
                                      <label className="block text-sm text-white/60 mb-1">
                                        {field.field_label}
                                        {field.is_required && <span className="text-red-400 ml-1">*</span>}
                                      </label>
                                      
                                      {/* Render based on field type */}
                                      {field.field_type === 'select' ? (
                                        <select
                                          value={attendee.custom_fields?.[field.field_name] || ''}
                                          onChange={(e) => updateAttendee(idx, 'custom_fields', {
                                            ...attendee.custom_fields,
                                            [field.field_name]: e.target.value
                                          })}
                                          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                                          data-testid={`attendee-${field.field_name}-${idx}`}
                                        >
                                          <option value="">{field.placeholder || 'Select...'}</option>
                                          {(field.field_options || []).map((opt, oidx) => (
                                            <option key={oidx} value={opt.value}>{opt.label}</option>
                                          ))}
                                        </select>
                                      ) : field.field_type === 'textarea' ? (
                                        <textarea
                                          value={attendee.custom_fields?.[field.field_name] || ''}
                                          onChange={(e) => updateAttendee(idx, 'custom_fields', {
                                            ...attendee.custom_fields,
                                            [field.field_name]: e.target.value
                                          })}
                                          placeholder={field.placeholder}
                                          rows={3}
                                          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                                          data-testid={`attendee-${field.field_name}-${idx}`}
                                        />
                                      ) : field.field_type === 'country' ? (
                                        <select
                                          value={attendee.custom_fields?.[field.field_name] || attendee.country || ''}
                                          onChange={(e) => {
                                            updateAttendee(idx, 'country', e.target.value);
                                            updateAttendee(idx, 'custom_fields', {
                                              ...attendee.custom_fields,
                                              [field.field_name]: e.target.value
                                            });
                                          }}
                                          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                                          data-testid={`attendee-${field.field_name}-${idx}`}
                                        >
                                          <option value="">Select country...</option>
                                          {COUNTRIES.map((country, cidx) => (
                                            <option key={cidx} value={country}>{country}</option>
                                          ))}
                                        </select>
                                      ) : field.field_type === 'checkbox' ? (
                                        <label className="flex items-center gap-2 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={attendee.custom_fields?.[field.field_name] || false}
                                            onChange={(e) => updateAttendee(idx, 'custom_fields', {
                                              ...attendee.custom_fields,
                                              [field.field_name]: e.target.checked
                                            })}
                                            className="w-4 h-4 text-primary rounded"
                                          />
                                          <span className="text-white/80">{field.placeholder || 'Yes'}</span>
                                        </label>
                                      ) : (
                                        <input
                                          type={field.field_type === 'phone' ? 'tel' : field.field_type === 'number' ? 'number' : field.field_type}
                                          value={
                                            field.field_name === 'organization' ? (attendee.organization || '') :
                                            field.field_name === 'job_title' ? (attendee.job_title || '') :
                                            field.field_name === 'phone' ? (attendee.phone || '') :
                                            (attendee.custom_fields?.[field.field_name] || '')
                                          }
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            // Map common fields to attendee root level
                                            if (field.field_name === 'organization') {
                                              updateAttendee(idx, 'organization', value);
                                            } else if (field.field_name === 'job_title') {
                                              updateAttendee(idx, 'job_title', value);
                                            } else if (field.field_name === 'phone') {
                                              updateAttendee(idx, 'phone', value);
                                            } else {
                                              updateAttendee(idx, 'custom_fields', {
                                                ...attendee.custom_fields,
                                                [field.field_name]: value
                                              });
                                            }
                                          }}
                                          placeholder={field.placeholder}
                                          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                                          data-testid={`attendee-${field.field_name}-${idx}`}
                                        />
                                      )}
                                      
                                      {field.help_text && (
                                        <p className="text-xs text-white/40 mt-1">{field.help_text}</p>
                                      )}
                                    </div>
                                  ))
                                }
                              </div>
                            </div>
                          )}
                          
                          {/* Fallback fields when no dynamic fields configured */}
                          {(!event?.registration_fields || event.registration_fields.length === 0) && (
                            <div className="grid sm:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm text-white/60 mb-1">Organization</label>
                                <input
                                  type="text"
                                  value={attendee.organization}
                                  onChange={(e) => updateAttendee(idx, 'organization', e.target.value)}
                                  className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                                />
                              </div>
                              <div>
                                <label className="block text-sm text-white/60 mb-1">Job Title</label>
                                <input
                                  type="text"
                                  value={attendee.job_title}
                                  onChange={(e) => updateAttendee(idx, 'job_title', e.target.value)}
                                  className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex gap-4 mt-6">
                    <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    <Button 
                      className="flex-1 bg-primary hover:bg-primary/90 text-black"
                      onClick={async () => {
                        // Create order first, then go to payment step
                        await submitOrder();
                        setStep(4);
                      }}
                      disabled={submitting || !buyerInfo.name || !buyerInfo.email || attendees.some(a => !a.name)}
                      data-testid="continue-to-payment"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating Order...
                        </>
                      ) : (
                        <>
                          Continue to Payment
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 4: Payment */}
              {step === 4 && (
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-white mb-6">Payment</h2>
                  
                  {/* Order Summary */}
                  <div className="mb-6 p-4 bg-background rounded-lg">
                    <h3 className="font-medium text-white mb-3">Order Summary</h3>
                    <div className="space-y-2 text-sm">
                      {ticketTypes.filter(t => selectedTickets[t.id] > 0).map(t => (
                        <div key={t.id} className="flex justify-between text-white/80">
                          <span>{t.name} x {selectedTickets[t.id]}</span>
                          <span>KES {(t.price * selectedTickets[t.id]).toLocaleString()}</span>
                        </div>
                      ))}
                      {discountAmount > 0 && (
                        <div className="flex justify-between text-green-500">
                          <span>Promo Discount</span>
                          <span>-KES {discountAmount.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-white font-semibold pt-2 border-t border-border">
                        <span>Total</span>
                        <span>KES {total.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Payment Methods */}
                  <div className="mb-6">
                    <h3 className="font-medium text-white mb-3">Payment Method</h3>
                    <div className="grid gap-3">
                      
                      {/* Paybill Option (Recommended) */}
                      <div 
                        className={`p-4 bg-background rounded-lg border-2 cursor-pointer transition-colors ${
                          paymentMethod === 'paybill' ? 'border-primary' : 'border-border hover:border-white/30'
                        }`}
                        onClick={() => setPaymentMethod('paybill')}
                        data-testid="paybill-option"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center text-white font-bold">
                            M
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-white">M-Pesa Paybill</p>
                              <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded">Recommended</span>
                            </div>
                            <p className="text-sm text-white/60">Manual payment via M-Pesa Paybill</p>
                          </div>
                          {paymentMethod === 'paybill' && <Check className="w-5 h-5 text-primary" />}
                        </div>
                        
                        {paymentMethod === 'paybill' && paybillInstructions && (
                          <div className="mt-4 space-y-4">
                            {/* Paybill Details */}
                            <div className="bg-card-bg rounded-lg p-4 space-y-3">
                              <div className="flex justify-between items-center">
                                <span className="text-white/60 text-sm">Paybill Number</span>
                                <span className="text-white font-mono font-bold text-lg">{paybillInstructions.paybill_number}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-white/60 text-sm">Account Number</span>
                                <span className="text-primary font-mono font-bold">{paybillInstructions.account_number}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-white/60 text-sm">Amount</span>
                                <span className="text-white font-bold">KES {parseInt(paybillInstructions.amount).toLocaleString()}</span>
                              </div>
                            </div>
                            
                            {/* Instructions */}
                            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                              <p className="text-yellow-400 text-sm font-medium mb-2">Payment Instructions:</p>
                              <ol className="text-white/70 text-sm space-y-1 list-decimal list-inside">
                                {paybillInstructions.instructions?.slice(0, 7).map((step, idx) => (
                                  <li key={idx}>{step.replace(/^\d+\.\s*/, '')}</li>
                                ))}
                              </ol>
                            </div>
                            
                            {/* M-Pesa Reference Input */}
                            <div>
                              <label className="block text-sm text-white/60 mb-2">M-Pesa Reference Code *</label>
                              <input
                                type="text"
                                value={mpesaReference}
                                onChange={(e) => setMpesaReference(e.target.value.toUpperCase())}
                                placeholder="e.g., QJK2XXXXXX"
                                className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 font-mono uppercase placeholder-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                                data-testid="mpesa-reference-input"
                              />
                              <p className="text-xs text-white/40 mt-1">Enter the reference code from your M-Pesa confirmation SMS</p>
                            </div>
                            
                            {/* Phone (optional) */}
                            <div>
                              <label className="block text-sm text-white/60 mb-2">Phone Number Used (optional)</label>
                              <input
                                type="tel"
                                value={mpesaPhone}
                                onChange={(e) => setMpesaPhone(e.target.value)}
                                placeholder="0712 345 678"
                                className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                                data-testid="paybill-phone-input"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* STK Push Option */}
                      <div 
                        className={`p-4 bg-background rounded-lg border-2 cursor-pointer transition-colors ${
                          paymentMethod === 'stk' ? 'border-primary' : 'border-border hover:border-white/30'
                        }`}
                        onClick={() => setPaymentMethod('stk')}
                        data-testid="stk-option"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-800 rounded-lg flex items-center justify-center text-white font-bold">
                            M
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-white">M-Pesa STK Push</p>
                            <p className="text-sm text-white/60">Automatic payment prompt to your phone</p>
                          </div>
                          {paymentMethod === 'stk' && <Check className="w-5 h-5 text-primary" />}
                        </div>
                        
                        {paymentMethod === 'stk' && (
                          <div className="mt-4">
                            <label className="block text-sm text-white/60 mb-2">M-Pesa Phone Number *</label>
                            <input
                              type="tel"
                              value={mpesaPhone}
                              onChange={(e) => setMpesaPhone(e.target.value)}
                              placeholder="0712 345 678"
                              className="w-full bg-card-bg border border-border rounded-lg px-4 py-3 text-white"
                              data-testid="mpesa-phone-input"
                            />
                            <p className="text-xs text-white/40 mt-1">You will receive an M-Pesa prompt on this number</p>
                            <div className="mt-2 p-2 bg-yellow-500/10 rounded text-yellow-400 text-xs">
                              Note: STK Push requires M-Pesa API configuration. If it fails, use Paybill option.
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Bank/EFT Transfer Option - Only show if configured */}
                      {event?.bank_account_number && (
                        <div 
                          className={`p-4 bg-background rounded-lg border-2 cursor-pointer transition-colors ${
                            paymentMethod === 'bank' ? 'border-primary' : 'border-border hover:border-white/30'
                          }`}
                          onClick={() => setPaymentMethod('bank')}
                          data-testid="bank-option"
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold">
                              <Building2 className="w-5 h-5" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-white">Bank Transfer / EFT</p>
                              <p className="text-sm text-white/60">Direct bank transfer via EFT or RTGS</p>
                            </div>
                            {paymentMethod === 'bank' && <Check className="w-5 h-5 text-primary" />}
                          </div>
                          
                          {paymentMethod === 'bank' && (
                            <div className="mt-4 space-y-4">
                              {/* Bank Details */}
                              <div className="bg-card-bg rounded-lg p-4 space-y-3">
                                <div className="flex justify-between items-start">
                                  <span className="text-white/60 text-sm">Bank</span>
                                  <span className="text-white font-medium text-right">{event.bank_name}</span>
                                </div>
                                <div className="flex justify-between items-start">
                                  <span className="text-white/60 text-sm">Account Name</span>
                                  <span className="text-white font-medium text-right max-w-[200px]">{event.bank_account_name}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-white/60 text-sm">Account Number</span>
                                  <span className="text-primary font-mono font-bold">{event.bank_account_number}</span>
                                </div>
                                {event.bank_swift_code && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-white/60 text-sm">SWIFT Code</span>
                                    <span className="text-white font-mono">{event.bank_swift_code}</span>
                                  </div>
                                )}
                                {event.bank_branch && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-white/60 text-sm">Branch</span>
                                    <span className="text-white">{event.bank_branch}</span>
                                  </div>
                                )}
                                <div className="flex justify-between items-center pt-2 border-t border-border">
                                  <span className="text-white/60 text-sm">Amount</span>
                                  <span className="text-white font-bold">KES {total.toLocaleString()}</span>
                                </div>
                              </div>
                              
                              {/* Instructions */}
                              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                                <p className="text-blue-400 text-sm font-medium mb-2">Bank Transfer Instructions:</p>
                                <ol className="text-white/70 text-sm space-y-1 list-decimal list-inside">
                                  <li>Log in to your bank's online/mobile banking</li>
                                  <li>Select "Transfer" or "Send Money"</li>
                                  <li>Enter the account details above</li>
                                  <li>Use your name or email as reference</li>
                                  <li>Complete the transfer and save the receipt</li>
                                </ol>
                              </div>
                              
                              {/* Bank Reference Input */}
                              <div>
                                <label className="block text-sm text-white/60 mb-2">Bank Reference / Receipt No. *</label>
                                <input
                                  type="text"
                                  value={mpesaReference}
                                  onChange={(e) => setMpesaReference(e.target.value.toUpperCase())}
                                  placeholder="e.g., TRF123456789"
                                  className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 font-mono uppercase placeholder-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                                  data-testid="bank-reference-input"
                                />
                                <p className="text-xs text-white/40 mt-1">Enter the transaction reference from your bank transfer</p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-4">
                    <Button variant="outline" onClick={() => setStep(3)} className="flex-1">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    
                    {(paymentMethod === 'paybill' || paymentMethod === 'bank') ? (
                      <Button 
                        className="flex-1 bg-primary hover:bg-primary/90 text-black"
                        onClick={confirmPaybillPayment}
                        disabled={confirmingPayment || !mpesaReference.trim()}
                        data-testid="confirm-paybill-btn"
                      >
                        {confirmingPayment ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Verifying...
                          </>
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Confirm Payment
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button 
                        className="flex-1 bg-primary hover:bg-primary/90 text-black"
                        onClick={submitOrder}
                        disabled={submitting || !mpesaPhone}
                        data-testid="complete-order-btn"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Pay KES {total.toLocaleString()}
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Step 5: Confirmation */}
              {step === 5 && order && (
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="w-8 h-8 text-green-500" />
                  </div>
                  
                  <h2 className="text-2xl font-semibold text-white mb-2">Order Confirmed!</h2>
                  <p className="text-white/60 mb-6">
                    Your order #{order.order_number} has been created successfully.
                  </p>
                  
                  {/* Tickets */}
                  <div className="bg-background rounded-lg p-4 mb-6 text-left">
                    <h3 className="font-medium text-white mb-3">Your Tickets</h3>
                    <div className="space-y-3">
                      {order.tickets?.map((ticket, idx) => (
                        <div key={idx} className="flex items-center gap-4 p-3 bg-card-bg rounded-lg">
                          {ticket.qr_code && (
                            <img 
                              src={`data:image/png;base64,${ticket.qr_code}`}
                              alt="Ticket QR"
                              className="w-16 h-16 rounded"
                            />
                          )}
                          <div className="flex-1">
                            <p className="font-medium text-white">{ticket.attendee_name}</p>
                            <p className="text-sm text-white/60">{ticket.ticket_number}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <p className="text-sm text-white/60 mb-6">
                    A confirmation will be sent to {buyerInfo.email}. 
                    Save your ticket for entry!
                  </p>
                  
                  <div className="flex flex-col gap-3">
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-6"
                      onClick={() => navigate(`/tickets/${order.id}`)}
                      data-testid="view-ticket-btn"
                    >
                      <Ticket className="w-4 h-4 mr-2" />
                      View Full Ticket
                    </Button>
                    <div className="flex gap-3">
                      <Button 
                        variant="outline" 
                        onClick={() => navigate('/events')}
                        className="flex-1"
                      >
                        Browse More Events
                      </Button>
                      <Button 
                        variant="outline"
                        className="flex-1"
                        onClick={() => window.print()}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Print
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
      
      {/* Waitlist Modal */}
      <AnimatePresence>
        {showWaitlistModal && event && (
          <WaitlistModal
            event={event}
            isOpen={showWaitlistModal}
            onClose={() => setShowWaitlistModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default EventDetailPage;
